import { isEqual } from 'lodash';
import { DatasetQueryModelFactory } from '../models';
import { DATASET_PROP, DEVIATIONS_PROP } from '../consts';
import * as QueryConst from '../utils/query-const';
import { extractQueryModelsParams } from './utils';

export const PanelDatasetMixin = {
    static() {
        return {
            DATASET_PROP,
            DEVIATIONS_PROP,
            ...QueryConst
        };
    },
    data: () => ({
        /**
         * @type {import('@goodt-common/data').QueryModel[]}
         */
        queryModels: [],
    }),
    computed: {
        /**
         *
         * @return {{getDimensions: ((function(): string[])|*)}}
         */
        $drilldown() {
            return {
                dimensions: this.queryModels.filter(Boolean).flatMap(({ drilldown }) => drilldown.dimensions),
                getDimensions: this.$drilldownGetDimensions
            };
        },
        /**
         * @return {import('@goodt-common/data').IDatasetRequestProps[]}
         */
        datasetRequests() {
            return this.props[this.DATASET_PROP] ?? [];
        },
        /**
         * @return {import('@goodt-common/data').QueryModel}
         */
        queryModel() {
            return this.queryModels[0];
        },
        /**
         * @public
         * @return {boolean}
         */
        hasDataset() {
            return this.datasetRequests.length > 0;
        },
        /**
         *
         * @return {string[]}
         */
        metrics() {
            return this.$queryGetParams(['metrics']);
        },
        /**
         *
         * @return {string[]}
         */
        dimensions() {
            return this.$queryGetParams(['dimensions']);
        },
        /**
         * @return {string[]}
         */
        dimensionsMetrics() {
            return this.$queryGetParams(['dimensions', 'metrics']);
        },
        /**
         *
         * @return {string[]}
         */
        fields() {
            return this.$queryGetParams(['fields']);
        },
        /**
         *
         * @return {string[]}
         */
        drilldowns() {
            return this.$queryGetParams(['drilldown.names']);
        },
        /**
         *
         * @return {string[]}
         */
        deviations() {
            return this.props[DEVIATIONS_PROP]?.rules.map(({ name }) => name) ?? [];
        }
    },

    watch: {
        datasetRequests: {
            handler: '$queryUpdateModels',
            immediate: true
        }
    },

    methods: {
        /**
         *
         * @param {import('@goodt-common/data').IDatasetRequestProps[]} newDatasetRequests
         * @param {import('@goodt-common/data').IDatasetRequestProps[]} [oldDatasetRequests=[]]
         */
        $queryUpdateModels(newDatasetRequests = this.datasetRequests, oldDatasetRequests = []) {
            if (newDatasetRequests.length === 0 && this.queryModels.length === 0) {
                return;
            }

            if (newDatasetRequests.length === 0 && this.queryModels.length > 0) {
                this.queryModels = [];
                return;
            }

            let isChanged = newDatasetRequests.length !== oldDatasetRequests.length;
            const queryModels = newDatasetRequests.map((datasetRequest, index) => {
                const newQuery = datasetRequest == null ? null : datasetRequest.query;
                const oldQuery = oldDatasetRequests[index] == null ? null : oldDatasetRequests[index].query;
                if (isEqual(newQuery, oldQuery)) {
                    return this.queryModels[index];
                }
                isChanged = true;
                if (newQuery === null) {
                    return null;
                }
                return DatasetQueryModelFactory(newQuery);
            });

            if (isChanged) {
                this.queryModels = queryModels;
            }
        },
        /**
         *
         * @param {string[]} items
         * @param {boolean} [empty=false]
         * @return {{ label: string, value: any}[]}
         */
        buildOptions(items, { empty = false } = {}) {
            const optional = empty === false ? [] : [empty === true ? { label: '', value: null } : empty];
            return [...optional, ...items.map((value) => ({ label: value ?? '', value }))];
        },
        /**
         *
         * @param {string[]} params
         * @return {string[]}
         */
        $queryGetParams(params) {
            return extractQueryModelsParams(
                this.queryModels.filter((queryModel) => queryModel != null),
                params
            );
        },
        /**
         *
         * @param {string} drilldownName
         * @return {string[]}
         */
        $drilldownGetDimensions(drilldownName) {
            // prettier-ignore
            const queryModel = this.queryModels
                .filter(Boolean)
                .find(({ drilldown }) => drilldown.has(drilldownName));
            if (queryModel === null) {
                return [];
            }

            return queryModel.drilldown.get(drilldownName).dimensions;
        }
    }
};

